import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from '@react-router';
import { Box, Flex, TextV2, ButtonV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useHoneymoonOffersPromo } from '@shared/components/HoneymoonOffersPromo/useHoneymoonOffersPromo';
import { buildHoneymoonLandingPath } from '@shared/utils/honeymoon/honeymoonLanding';
import { FadeInWrapper, HoneymoonBottomSheetStyle, StyledBanner, StyledHeroImage } from './HoneymoonFundSuccess.styles';
import { useHoneymoonFundBalanceCopy } from './useHoneymoonFundBalanceCopy';

const HERO_IMAGE_URL = 'https://withjoy.com/assets/public/registry/cash%20fund%20flow/honeymoon-visual.png';

type HoneymoonFundSuccessProps = Readonly<{
  fundTitle: string;
  onRemindLater: () => void;
  onStartExploring: () => void;
  onView: () => void;
}>;

type HoneymoonFundSuccessCopyProps = Readonly<{
  isVisible: boolean;
  headline: React.ReactNode;
  body: React.ReactNode;
  ctaLabel: React.ReactNode;
  onCtaClick: () => void;
}>;

const HoneymoonFundSuccessCopy: React.FC<HoneymoonFundSuccessCopyProps> = ({ isVisible, headline, body, ctaLabel, onCtaClick }) => (
  <Flex
    flexDirection="column"
    alignItems="center"
    style={{ gridArea: '1 / 1', visibility: isVisible ? 'visible' : 'hidden' }}
  >
    <TextV2
      typographyVariant="hed5"
      textAlign="center"
      marginTop={7}
      marginBottom={3}
    >
      {headline}
    </TextV2>

    <TextV2
      typographyVariant="body1"
      textAlign="center"
      color="mono10"
      marginBottom={6}
      maxWidth="420px"
    >
      {body}
    </TextV2>

    <ButtonV2
      intent="neutral"
      shape="rounded"
      width={{ _: '100%', md: 'auto' }}
      minWidth="18rem"
      onClick={onCtaClick}
      marginBottom={3}
    >
      {ctaLabel}
    </ButtonV2>
  </Flex>
);

export const HoneymoonFundSuccess: React.FC<HoneymoonFundSuccessProps> = ({ fundTitle, onRemindLater, onStartExploring, onView }) => {
  const { tq } = useTranslation('sharedRegistry');
  const history = useHistory();
  const { eventHandle = '' } = useEventInfo();
  const { isEnabled, isLoading, fundBalanceDisplay, fundCouponSavingsDisplay, fundCouponPercentOff } = useHoneymoonOffersPromo({ eventHandle });
  const hasCashFundBalance = isEnabled && !!fundBalanceDisplay;
  const balanceCopy = useHoneymoonFundBalanceCopy({ fundBalanceDisplay: fundBalanceDisplay ?? '', fundCouponSavingsDisplay, fundCouponPercentOff });
  const landingUrl = useMemo(() => buildHoneymoonLandingPath(eventHandle, 'honeymoon_fund_add_success'), [eventHandle]);

  useEffect(() => {
    onView();
  }, [onView]);

  const handleStartExploring = useCallback(() => {
    onStartExploring();
    history.push(landingUrl);
  }, [onStartExploring, history, landingUrl]);

  return (
    <FadeInWrapper data-honeymoon-success-marker="true">
      <HoneymoonBottomSheetStyle />
      <Flex
        flexDirection="column"
        alignItems="center"
        paddingX={[5, 7]}
        paddingTop={2}
        paddingBottom={5}
        maxWidth="480px"
        marginX="auto"
      >
        <StyledBanner>
          <TextV2
            typographyVariant="label2"
            color="positive7"
            textAlign="center"
          >
            {tq`Your honeymoon cash fund has been created.`}
          </TextV2>
        </StyledBanner>

        <StyledHeroImage
          src={HERO_IMAGE_URL}
          alt={fundTitle}
        />

        {/* While loading, both hidden versions share one grid cell to hold the space; after, only the shown one stays, so no spare gap. */}
        <Box
          display="grid"
          width="100%"
        >
          {(isLoading || !hasCashFundBalance) && (
            <HoneymoonFundSuccessCopy
              isVisible={!isLoading}
              headline={tq`Your honeymoon starts here!`}
              body={tq`You've got a cash fund ready to go. Browse destinations, save favorites, and when you're ready to book — you'll get up to 30% off as a Joy member.`}
              ctaLabel={tq`Start exploring destinations`}
              onCtaClick={handleStartExploring}
            />
          )}
          {(isLoading || hasCashFundBalance) && (
            <HoneymoonFundSuccessCopy
              isVisible={!isLoading}
              headline={balanceCopy.headline}
              body={balanceCopy.body}
              ctaLabel={balanceCopy.ctaLabel}
              onCtaClick={handleStartExploring}
            />
          )}
        </Box>

        <ButtonV2
          variant="ghost"
          intent="neutral"
          onClick={onRemindLater}
        >
          {tq`Remind me later`}
        </ButtonV2>
      </Flex>
    </FadeInWrapper>
  );
};
